import React, { useEffect, useState } from 'react';
import { FlatList, Pressable, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import type { EmployeeSummary, ID } from '@hrms/shared';
import { api } from '../../../lib/api';
import { useDebounced } from '../../../lib/hooks';
import { Avatar, Field, Icon, IconButton, Loading, Modal, Text, TextField, radius, useInputStyle, useTheme } from '../../../ui';

/**
 * Searchable employee picker: a field that opens a modal list backed by
 * employees.list({ search }). Works on phones (bottom sheet) and desktop (dialog).
 */
export function EmployeePicker({
  label,
  value,
  onChange,
  placeholder = 'Search employee…',
  required,
  error,
  hint,
  status,
  clearable = true,
  testID,
}: {
  label?: string;
  value: ID | null | undefined;
  onChange: (id: ID | null, emp?: EmployeeSummary) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  status?: string;
  clearable?: boolean;
  testID?: string;
}) {
  const { colors } = useTheme();
  const base = useInputStyle(!!error);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [selected, setSelected] = useState<EmployeeSummary | null>(null);
  const dq = useDebounced(text, 250);
  const q = useQuery({ queryKey: ['employees', 'picker', dq, status], queryFn: () => api.employees.list({ search: dq || undefined, per_page: 20, status }), enabled: open });
  const known = useQuery({ queryKey: ['employee', value], queryFn: () => api.employees.get(value as ID), enabled: !!value && selected?.id !== value });
  useEffect(() => {
    if (known.data && known.data.id === value) setSelected(known.data);
  }, [known.data, value]);
  useEffect(() => {
    if (!value) setSelected(null);
  }, [value]);
  const shown = selected && selected.id === value ? `${selected.full_name} (${selected.emp_code})` : value ? `#${value}` : '';
  const pick = (e: EmployeeSummary) => {
    setSelected(e);
    onChange(e.id, e);
    setOpen(false);
    setText('');
  };
  return (
    <Field label={label} required={required} error={error} hint={hint}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
        <Pressable testID={testID} accessibilityRole="button" accessibilityLabel={`${label ?? 'Employee'}: ${shown || placeholder}`} onPress={() => setOpen(true)} style={[base, { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 }]}>
          <Icon name="user" size={16} color={colors.muted} />
          <Text numberOfLines={1} style={{ flex: 1 }} color={shown ? colors.text : colors.faint}>
            {shown || placeholder}
          </Text>
        </Pressable>
        {clearable && !!value && <IconButton icon="x" size="sm" label="Clear employee" onPress={() => onChange(null)} />}
      </View>
      <Modal open={open} onClose={() => setOpen(false)} title={label ?? 'Select employee'} width={460} noScroll>
        <View style={{ padding: 12, paddingBottom: 4 }}>
          <TextField placeholder="Name, code or email…" value={text} onChangeText={setText} autoFocus accessibilityLabel="Search employees" />
        </View>
        {q.isLoading ? (
          <Loading />
        ) : (
          <FlatList
            data={q.data?.data ?? []}
            keyExtractor={(e) => String(e.id)}
            style={{ flexGrow: 0 }}
            contentContainerStyle={{ padding: 8 }}
            keyboardShouldPersistTaps="handled"
            renderItem={({ item }) => {
              const sel = item.id === value;
              return (
                <Pressable
                  accessibilityRole="button"
                  onPress={() => pick(item)}
                  style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10, borderRadius: radius.md, backgroundColor: sel ? colors.primarySoft : pressed ? colors.hover : 'transparent' })}
                >
                  <Avatar name={item.full_name} size={28} />
                  <View style={{ flex: 1 }}>
                    <Text weight="medium">{item.full_name}</Text>
                    <Text variant="small">
                      {item.emp_code}
                      {item.designation ? ` · ${item.designation}` : ''}
                    </Text>
                  </View>
                  {sel && <Icon name="check" size={16} color={colors.primary} />}
                </Pressable>
              );
            }}
            ListEmptyComponent={<Text variant="muted" style={{ padding: 12 }}>No employees found</Text>}
          />
        )}
      </Modal>
    </Field>
  );
}
